'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState, useEffect, startTransition } from 'react';
import { ContactModal } from './ContactModal';
import { Icon } from './Icon';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LocaleOverlay } from './menu/LocaleOverlay';
import { MainMenu } from './menu/MenuOverlay';
import { SectionMenu } from './menu/SectionMenu';
import { Link } from '@/components/Link';
import { usePathname } from '@/i18n/navigation';
import type { LocationDto } from '@/lib/api';
import { withAssetPrefix } from '@/lib/assetPrefix';
import { GaButtonSection } from '@/lib/gtm/gaButtonSection';
import { GaContactType } from '@/lib/gtm/gaContactType';
import { GaEventCategory } from '@/lib/gtm/gaEventCategory';
import { pushGaEvent } from '@/lib/gtm/pushGaEvent';
import { useShellState } from '@/lib/useShellState';
import type { MenuTree } from '@/types/menu';

interface HeaderProps {
  phone?: string;
  matelsoPhoneLabel?: string;
  advisoryText?: string;
  /**
   * The advisory text is the Page's own label (e.g. the City-Jobs
   * `Nachhilfe geben in {city.name}`), which takes the phone's place on
   * desktop: the phone link is hidden from `lg` up, as on the legacy page
   * (10.03.01.01 § AC 7). Mobile keeps its phone icon.
   */
  advisoryReplacesPhone?: boolean;
  initialMenu: MenuTree | null;
  locales?: string[];
  currentLocale?: string;
  /**
   * A campaign variant B/C Page pins the header to its scrolled (compact)
   * state. A property of the Page, not of the visit (SH-471): server-rendered
   * for the first page, kept fresh across client-side navigations from
   * `getShellState`.
   */
  forceScrolled?: boolean;
  /**
   * 10.03.01.01 § AC 8: the route's "starts scrolled" rule for the pathname
   * the layout rendered. Server-rendered so the first paint is already right;
   * kept fresh across client-side navigations from `getShellState`.
   */
  startsScrolled?: boolean;
  /**
   * Current location, when the layout resolved one (Standort pages).
   * Forwarded to `ContactModal` so the Adjustable Contact Form gets its
   * render-context location (customerNumber center-number prefix and
   * `locationSlug` fallback — supersedes AM-SH-378's `centerNumber` prop).
   * Server-rendered for the first page; kept fresh across client-side
   * navigations from `getShellState`, or the modal would key its customer
   * number on a Standort the visitor has already left.
   */
  location?: LocationDto;
  /**
   * The brand's Domain Setting placeholders, forwarded to `ContactModal`. The
   * header mounts outside the block pipeline, so the modal builds its own
   * render context and would otherwise resolve no `{domain.*}` token at all.
   */
  domainPlaceholders?: Record<string, string>;
}

const SCROLL_THRESHOLD = 20;

export const Header = ({
  phone: initialPhone,
  matelsoPhoneLabel: initialMatelsoPhoneLabel,
  advisoryText: initialAdvisoryText,
  advisoryReplacesPhone: initialAdvisoryReplacesPhone = false,
  domainPlaceholders,
  initialMenu,
  locales,
  currentLocale,
  forceScrolled = false,
  startsScrolled = false,
  location: initialLocation,
}: HeaderProps) => {
  const t = useTranslations('header');
  const tContact = useTranslations('contact');
  const pathname = usePathname();

  // Next.js does not re-run the server layout on a soft navigation, so every
  // prop here is only ever the value of the page the visitor first landed on.
  // `useShellState` refreshes the whole slice for each new pathname, in the
  // one round trip it shares with the Footer and the Sticky CTA Bar.
  const {
    menu,
    phone,
    matelsoPhoneLabel,
    advisoryText,
    advisoryReplacesPhone,
    headerStartsScrolled: routeStartsScrolled,
    campaignForcesScrolled,
    location,
  } = useShellState(
    {
      menu: initialMenu,
      location: initialLocation,
      phone: initialPhone,
      matelsoPhoneLabel: initialMatelsoPhoneLabel,
      advisoryText: initialAdvisoryText,
      advisoryReplacesPhone: initialAdvisoryReplacesPhone,
      headerStartsScrolled: startsScrolled,
      campaignForcesScrolled: forceScrolled,
    },
    (state) => ({
      menu: state.menu,
      location: state.location ?? undefined,
      phone: state.phone,
      matelsoPhoneLabel: state.matelsoPhoneLabel,
      advisoryText: state.advisoryText,
      advisoryReplacesPhone: state.advisoryReplacesPhone,
      headerStartsScrolled: state.headerStartsScrolled,
      campaignForcesScrolled: state.campaignForcesScrolled,
    }),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [scrolledByScroll, setScrolledByScroll] = useState(false);
  const alwaysScrolled = campaignForcesScrolled || routeStartsScrolled;
  const scrolled = alwaysScrolled || scrolledByScroll;

  const isLocationPage = pathname.startsWith('/nachhilfe/');
  const overlayOpen = menuOpen || localeOpen;
  const items = menu?.items ?? [];
  // The Menu's Header Contact Modal block controls the mail icon: no block
  // configured on the active menu ⇒ no icon and no modal.
  const headerContactModal = menu?.headerContactModal ?? null;

  useEffect(() => {
    if (alwaysScrolled) {
      return;
    }
    const onScroll = () =>
      setScrolledByScroll(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [alwaysScrolled]);

  useEffect(() => {
    startTransition(() => {
      setMenuOpen(false);
      setLocaleOpen(false);
    });
  }, [pathname]);

  return (
    <>
      <header
        className={[
          // `site-header` is not a utility — it is the scope `globals.css`
          // hangs the open menu's viewport cap and the page scroll lock on.
          'site-header top-0 right-0 left-0 z-50',
          overlayOpen
            ? 'transition-[box-shadow] duration-300'
            : 'transition-[background-color,box-shadow] duration-300',
          alwaysScrolled
            ? 'sticky bg-white shadow-md'
            : scrolled || overlayOpen
              ? 'fixed bg-white shadow-md'
              : 'absolute',
          scrolled ? 'text-foreground' : 'text-white',
        ].join(' ')}
      >
        <div
          className={[
            'pointer-events-none absolute inset-0 h-full bg-linear-to-b from-[#222628]/5 to-transparent transition-opacity duration-300',
            scrolled || overlayOpen ? 'opacity-0' : 'opacity-100',
          ].join(' ')}
        />

        {/*
          `w-full` matters only while the header is a flex column (an open
          menu, see `.site-header` in globals.css): an `auto` cross-axis
          margin stops a flex item stretching, so `mx-auto` alone would shrink
          this row to the width of the logo and the icons and centre it. As a
          block it is full-width either way.
        */}
        <div className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-4 pb-2 md:items-start lg:px-9">
          <div
            className={[
              'bg-primary flex shrink-0 items-center justify-center transition-all duration-300',
              scrolled
                ? 'rounded-b-lg p-2'
                : 'w-[180px] rounded-b-xl p-5 px-6 py-4 lg:min-w-[200px]',
            ].join(' ')}
          >
            <Link href="/">
              <Image
                src={withAssetPrefix('/schuelerhilfe-logo.svg')}
                alt="Schülerhilfe"
                width={153}
                height={70}
                className={[
                  'h-auto transition-all duration-300',
                  scrolled ? 'w-24 lg:w-30' : 'w-38 lg:w-50',
                ].join(' ')}
                // Eager, not preloaded: a preload competes with the hero (the
                // LCP element) for bandwidth, and the parser finds this anyway.
                loading="eager"
              />
            </Link>
          </div>

          <div
            className={[
              'ml-auto flex items-center gap-4 pt-0 md:pt-3 lg:gap-8',
              overlayOpen ? 'text-foreground' : '',
            ].join(' ')}
          >
            {advisoryText && (
              <p className="m-0 hidden text-base font-medium lg:block">
                {advisoryText}
              </p>
            )}

            {headerContactModal && (
              <button
                onClick={() => {
                  pushGaEvent({
                    event_category: GaEventCategory.ClickKontakt,
                    contact_type: GaContactType.Email,
                    button_section: GaButtonSection.Header,
                    contact_details: 'Nachricht schreiben',
                  });
                  setContactOpen(true);
                }}
                aria-label={headerContactModal.title || tContact('title')}
                className="flex items-center gap-2 font-bold transition-opacity hover:opacity-80"
              >
                <Icon src="/icons/svg/mail-holo.svg" size={20} />
              </button>
            )}

            {phone && (
              <a
                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                onClick={() =>
                  pushGaEvent({
                    event_category: GaEventCategory.ClickKontakt,
                    contact_type: GaContactType.Telefon,
                    button_section: GaButtonSection.Header,
                    contact_details: matelsoPhoneLabel || phone,
                  })
                }
                aria-label={`Telefon ${matelsoPhoneLabel || phone}`}
                className={[
                  'flex items-center gap-2 font-bold transition-opacity hover:opacity-80',
                  advisoryText && advisoryReplacesPhone ? 'lg:hidden' : '',
                ].join(' ')}
              >
                <Icon src="/icons/svg/phone-filled.svg" size={20} />
                <span className="hidden lg:inline">
                  {matelsoPhoneLabel || phone}
                </span>
              </a>
            )}

            {currentLocale && locales && locales.length > 1 && (
              <LanguageSwitcher
                currentLocale={currentLocale}
                isOpen={localeOpen}
                onToggle={() => {
                  setLocaleOpen((v) => !v);
                  setMenuOpen(false);
                }}
              />
            )}

            <button
              onClick={() => {
                setMenuOpen((v) => !v);
                setLocaleOpen(false);
              }}
              aria-label={menuOpen ? t('closeMenu') : t('openMenu')}
              aria-expanded={menuOpen}
              aria-controls={
                isLocationPage ? 'section-navigation' : 'main-navigation'
              }
              className="flex items-center p-1 transition-opacity hover:opacity-80"
            >
              <Icon
                src={
                  menuOpen
                    ? '/icons/svg/close.svg'
                    : '/icons/svg/burger-holo.svg'
                }
                size={24}
              />
            </button>
          </div>
        </div>

        {/*
          All three navs are mounted whatever their open state and hide
          themselves with the `hidden` attribute (SH-528). They used to be
          mounted conditionally, which kept every header link out of the server
          HTML — this site uses the burger menu at every breakpoint, so there
          was no second, always-visible nav for a crawler to follow.

          The open states still *exclude* each other: opening the locale
          switcher hides the main menu, as before. Only the mounting changed,
          not the behaviour.
        */}
        {currentLocale && locales && locales.length > 1 && (
          <LocaleOverlay
            locales={locales}
            currentLocale={currentLocale}
            isOpen={localeOpen}
            onClose={() => setLocaleOpen(false)}
          />
        )}

        {isLocationPage ? (
          <SectionMenu
            key={menuOpen ? 'open' : 'closed'}
            items={items}
            menuTracking={menu?.tracking}
            phone={phone}
            matelsoPhoneLabel={matelsoPhoneLabel}
            isOpen={menuOpen && !localeOpen}
            onClose={() => setMenuOpen(false)}
          />
        ) : (
          <MainMenu
            key={menuOpen ? 'open' : 'closed'}
            items={items}
            menuTracking={menu?.tracking}
            phone={phone}
            matelsoPhoneLabel={matelsoPhoneLabel}
            isOpen={menuOpen && !localeOpen}
            onClose={() => setMenuOpen(false)}
          />
        )}
      </header>

      {headerContactModal && (
        <ContactModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          config={headerContactModal}
          location={location}
          domainPlaceholders={domainPlaceholders}
          phone={phone}
          matelsoPhoneLabel={matelsoPhoneLabel}
        />
      )}
    </>
  );
};
